import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Power, PowerOff, Users, User, Shield, Building2, Globe, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useFormularioDistribuicao } from "@/hooks/useFormularioDistribuicao";
import { PageLoading } from "@/components/ui/page-loading";

interface FormularioDistribuicoesListProps {
  formularioId: string;
  onBack?: () => void;
}

export function FormularioDistribuicoesList({ formularioId, onBack }: FormularioDistribuicoesListProps) {
  const { distribuicoes, isLoading, reenviarNotificacoes, isReenviando, desativarDistribuicao, isDesativando, getEstatisticas } = useFormularioDistribuicao(formularioId);
  const [stats, setStats] = useState<Record<string, any>>({});

  useEffect(() => {
    const loadStats = async () => {
      const newStats: Record<string, any> = {};
      for (const dist of distribuicoes) {
        const stat = await getEstatisticas(dist.id);
        newStats[dist.id] = stat;
      }
      setStats(newStats);
    };

    if (distribuicoes.length > 0) {
      loadStats();
    }
  }, [distribuicoes]);

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'usuario': return <User className="h-4 w-4" />;
      case 'grupo': return <Users className="h-4 w-4" />;
      case 'role': return <Shield className="h-4 w-4" />;
      case 'client': return <Building2 className="h-4 w-4" />;
      case 'todos': return <Globe className="h-4 w-4" />;
      default: return null;
    }
  };

  const getTipoLabel = (tipo: string) => {
    switch (tipo) {
      case 'usuario': return 'Usuário Específico';
      case 'grupo': return 'Grupo';
      case 'role': return 'Função';
      case 'client': return 'Cliente';
      case 'todos': return 'Todos';
      default: return tipo;
    }
  };

  if (isLoading) {
    return <PageLoading />;
  }

  if (distribuicoes.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            Nenhuma distribuição encontrada para este formulário
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {onBack && (
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      )}
      
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Distribuições</CardTitle>
        </CardHeader>
        <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tipo</TableHead>
              <TableHead>Destinatários</TableHead>
              <TableHead>Taxa de Resposta</TableHead>
              <TableHead>Obrigatório</TableHead>
              <TableHead>Prazo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Enviado</TableHead>
              <TableHead>Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {distribuicoes.map((dist) => {
              const stat = stats[dist.id];
              return (
                <TableRow key={dist.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getTipoIcon(dist.tipo_destinatario)}
                      <span>{getTipoLabel(dist.tipo_destinatario)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {stat ? (
                      <span className="font-medium">{stat.total_destinatarios}</span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {stat ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">
                            {stat.total_respondidos}/{stat.total_destinatarios}
                          </span>
                          <Badge variant={stat.taxa_resposta >= 80 ? "default" : stat.taxa_resposta >= 50 ? "secondary" : "destructive"}>
                            {stat.taxa_resposta.toFixed(0)}%
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dist.obrigatorio ? (
                      <Badge variant="destructive">Sim</Badge>
                    ) : (
                      <Badge variant="secondary">Não</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {dist.data_limite ? (
                      <span className="text-sm">
                        {format(new Date(dist.data_limite), "dd/MM/yyyy", { locale: ptBR })}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Sem prazo</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {dist.ativo ? (
                      <Badge variant="default" className="flex items-center gap-1 w-fit">
                        <Power className="h-3 w-3" />
                        Ativo
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="flex items-center gap-1 w-fit">
                        <PowerOff className="h-3 w-3" />
                        Inativo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">
                        {format(new Date(dist.enviado_em), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        por {dist.enviado_por}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {dist.ativo && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reenviarNotificacoes(dist.id)}
                          disabled={isReenviando}
                        >
                          <Send className="h-3 w-3 mr-1" />
                          Reenviar
                        </Button>
                      )}
                      {dist.ativo && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => desativarDistribuicao(dist.id)}
                          disabled={isDesativando}
                        >
                          <PowerOff className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    </div>
  );
}
